import { ReactElement, useEffect } from 'react';
import { Anchor } from '@twilio-paste/core/anchor';
import { Box } from '@twilio-paste/core/box';
import { Heading } from '@twilio-paste/core/heading';
import { UnorderedList, ListItem } from '@twilio-paste/core/list';

import { EmptyState } from '@twiliointernal/e2-commons-shared-ui';
import {
  EmptyStateVariant,
  dataTestIds,
} from '@twiliointernal/e2-commons-shared-definitions';
import ReactHelmet from '../reactHelmet/reactHelmet';
import { getArticleSnippet } from '../../utils';
import { constants, copy } from './definitions';
import { ArticleLoaders } from './sub';
import ContactSupportCallout from '../contactSupportCallout';
import HtmlPasteParser from '../htmlPasteParser';
import { useFetchArticle, useKnowledgeBaseIdFromSlug } from '../../hooks';
import { logger } from '@twiliointernal/e2-commons-shared-libs';

import styles from './article.module.css';

const Article = (): ReactElement => {
  // this redirect is needed to avoid making contractural ammendments
  // https://twilio-engineering.atlassian.net/browse/SVC-6262
  useEffect(() => {
    if (
      window.location.href.includes(
        '4405290603803-Revised-Data-Protection-Addendum-for-new-EU-Standard-Contractual-Clauses',
      )
    ) {
      window.location.replace(
        'https://www.twilio.com/en-us/legal/privacy/faqs',
      );
    }
  });

  const { id: articleId, hash } = useKnowledgeBaseIdFromSlug();
  const {
    data: article,
    isLoading,
    error: articleError,
  } = useFetchArticle(articleId);
  const isArticleNotFound =
    article?.constructor === Object && Object.keys(article)?.length === 0;
  const isArticleBodyEmpty = !article?.body;

  useEffect(() => {
    if (isLoading && hash) {
      // Timeout needed to allow the hash element to render should it exist
      setTimeout(() => handleScrollToHashElement(), 1000);
    }
  }, [isLoading]);

  // Scrolls to the top of the page when routing to another article
  useEffect(() => {
    const scollContainer = document.querySelector(
      'div[class*="paste-theme-provider"]',
    );
    // Scroll container will always be present
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    scollContainer!.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [articleId]);

  const handleScrollToHashElement = (): void => {
    const hashElement = document.getElementById(hash);

    hashElement?.scrollIntoView();
  };

  useEffect(() => {
    if (articleError) {
      logger.error(`${constants.LOG_PREFIX}::${copy.errors.log(articleId)}`);
    }
  }, [articleError, articleId]);

  if (articleError) {
    <Box as="main" className={styles.container}>
      <Box as="article" className={styles.contentContainer}>
        <EmptyState
          heading={copy.emptyState.oops.heading}
          description={copy.emptyState.oops.description}
          variant={EmptyStateVariant.oops}
          maxWidth="47rem"
        ></EmptyState>
      </Box>
    </Box>;
  }

  if (isLoading) {
    return (
      <Box as="main" className={styles.container}>
        <Box as="article" className={styles.contentContainer}>
          <ArticleLoaders />
        </Box>
      </Box>
    );
  }

  if (isArticleNotFound) {
    return (
      <Box as="main" className={styles.container}>
        <Box as="article" className={styles.contentContainer}>
          <EmptyState
            heading={copy.emptyState.noResult.heading}
            description={copy.emptyState.noResult.description}
            variant={EmptyStateVariant.noResults}
            maxWidth="47rem"
          ></EmptyState>
        </Box>
      </Box>
    );
  }

  const hasAttachments = article && article?.attachments?.length > 0;

  return (
    <Box as="main" className={styles.container}>
      <ReactHelmet
        content={article?.body && getArticleSnippet(article?.body)}
        title={copy.reactHelmet.title(article?.title)}
      />
      <Box as="article" className={styles.contentContainer}>
        <Heading as="h2" variant="heading20">
          {article?.title}
        </Heading>
        <Box as="section" data-testid={dataTestIds.articleBody}>
          <HtmlPasteParser htmlString={article?.body || ''} />

          {hasAttachments && (
            <UnorderedList>
              {article?.attachments?.map((attachment) => (
                <ListItem key={attachment.fileName}>
                  <Anchor
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {attachment.fileName}
                  </Anchor>
                </ListItem>
              ))}
            </UnorderedList>
          )}
          {isArticleBodyEmpty && (
            <Box className={styles.emptyBodyContainer}>
              <EmptyState
                heading={copy.emptyState.bodyEmpty.heading}
                description={copy.emptyState.bodyEmpty.description}
                variant={EmptyStateVariant.noData}
                maxWidth="47rem"
              />
            </Box>
          )}
        </Box>
        <ContactSupportCallout />
      </Box>
    </Box>
  );
};

export default Article;
